import type { ComponentType, ReactNode } from 'react';
import type { Benutzer, Bereich, MeinProjekt, Stufe } from '@beholder/shared';

/**
 * MODULVERTRAG (Web) – so erscheint ein Teilsystem in der Oberfläche.
 *
 * Jedes Teilsystem liegt in web/src/modules/<bereich>/index.tsx und exportiert als default ein `WebModul`.
 * Der Kern findet es selbst. Die Seite erscheint als Reiter im Projekt unter /p/<projekt>/<bereich>/…
 * – aber nur für Benutzer mit mindestens Stufe 'lesen' in diesem Bereich.
 */
export interface WebModul {
  id: Bereich;
  name: string;
  /** Position der Reiter (klein = links). Mindmap 10, Aufgaben 20, Whiteboard 30, Budget 40, Mail 50, KI 90. */
  reihenfolge: number;
  symbol: ReactNode;
  /** Hauptseite des Bereichs. Unterseiten selbst mit <Routes> darin anlegen (relativ). */
  Seite?: ComponentType<ModulSeiteProps>;
  /** Kleine Karte für die Projektübersicht. */
  Karte?: ComponentType<ModulSeiteProps>;
  /** Wird auf jeder Projektseite zusätzlich gezeichnet (z. B. Chat-Knopf unten rechts). */
  Ueberall?: ComponentType<ModulSeiteProps>;
  /** Karte auf der Startseite (projektübergreifend, z. B. „Meine Aufgaben“). */
  Startkarte?: ComponentType<GlobalProps>;
  /** Projektübergreifende Seite unter /modul/<bereich>/… (z. B. Mail-Posteingang „Unsortiert“), mit Eintrag in der Kopfleiste. */
  GlobalSeite?: ComponentType<GlobalProps>;
  /** Name in der Kopfleiste für die GlobalSeite (Standard: name). */
  globalName?: string;
  /** Wer die GlobalSeite sieht (Standard: alle Angemeldeten). Der Server prüft trotzdem selbst. */
  globalSichtbar?(ich: Benutzer): boolean;
  /** Wird auf jeder Seite nach der Anmeldung gezeichnet (z. B. Hinweis „heute fällig“). Selbst dezent halten. */
  UeberallGlobal?: ComponentType<GlobalProps>;
}

export interface GlobalProps {
  ich: Benutzer;
}

export interface ModulSeiteProps {
  projekt: MeinProjekt;
  ich: Benutzer;
  /** Stufe im eigenen Bereich. */
  stufe: Stufe;
  darfSchreiben: boolean;
  /** Projekt neu laden (z. B. nach Änderung der Einstellungen). */
  neuLaden(): void;
}

const gefunden = import.meta.glob<{ default: WebModul }>('../modules/*/index.tsx', { eager: true });

export const WEB_MODULE: WebModul[] = Object.values(gefunden)
  .map((m) => m.default)
  .filter((m): m is WebModul => !!m?.id)
  .sort((a, b) => a.reihenfolge - b.reihenfolge);
